import { useState, useMemo } from "react";

import { useSelector } from "react-redux";
// eslint-disable-next-line import/no-named-as-default
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { StyledTab, TabList, TabPanel, Tabs } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { countryTextByIdFilter } from "app/filters/ngFilters";
import { getTableSelectedItems } from "app/reducers/_reducers/tableSelection.reducer";
import type { RootState } from "single-spa/store/types";

import { METRIC_CONFIGURATION, METRIC_KEYS } from "../config/tabsConfig";
import { useGeographyGraphData } from "../hooks/geographyQueries";
import type { useProcessedGeographyTableData } from "../hooks/useProcessedGeographyTableData";
import type { SelectedCountry } from "../types";
import { transformChartDataToSeries, generateLegendItems, filterChartData } from "../utils/chartUtils";

import { IndustryAnalysisGeographyTrafficAndEngagementChart } from "./IndustryAnalysisGeographyTrafficAndEngagementChart";

const StyledTabPanel = styled(TabPanel)`
    padding: 24px;
    background: ${colorsPalettes.carbon[0]};
`;

type TableDataState = ReturnType<typeof useProcessedGeographyTableData>;

interface IIndustryAnalysisGeographyTrafficAndEngagementTabsProps {
    tableDataState: TableDataState;
}

export const IndustryAnalysisGeographyTrafficAndEngagementTabs = ({
    tableDataState,
}: IIndustryAnalysisGeographyTrafficAndEngagementTabsProps) => {
    const [currentTabIndex, setCurrentTabIndex] = useState(0);
    const t = useTranslation();
    const subtitleFilters = useSubtitleFilters("date", "webSource");

    // Fetch data directly using hooks
    const { data: graphData, isLoading } = useGeographyGraphData();
    const { originalOrderedCountryIds } = tableDataState;
    const rawSelectedCountries = useSelector((state: RootState) =>
        getTableSelectedItems("IndustryAnalysisGeoTable")(state),
    ) as SelectedCountry[];

    /**
     * Ensure stable ordering of selected countries to prevent legend shuffling during table search.
     * This sorts selected countries by their position in the original (unfiltered) table order,
     * maintaining consistent legend display regardless of search filtering.
     */
    const selectedCountries = useMemo(() => {
        if (!rawSelectedCountries || !originalOrderedCountryIds) {
            return rawSelectedCountries;
        }

        return rawSelectedCountries.toSorted((a, b) => {
            const posA = originalOrderedCountryIds.indexOf(a.Country);
            const posB = originalOrderedCountryIds.indexOf(b.Country);

            // Items not found in reference (-1) should be placed at the end
            const adjustedPosA = posA === -1 ? Number.MAX_SAFE_INTEGER : posA;
            const adjustedPosB = posB === -1 ? Number.MAX_SAFE_INTEGER : posB;

            return adjustedPosA - adjustedPosB;
        });
    }, [rawSelectedCountries, originalOrderedCountryIds]);

    const handleSelectTab = (index: number) => {
        setCurrentTabIndex(index);
    };

    const currentMetricKey = METRIC_KEYS[currentTabIndex];
    const currentMetricConfig = METRIC_CONFIGURATION[currentMetricKey];
    const currentMetricData = graphData?.[currentMetricConfig.dataKey];

    const { chartSeries, legends } = useMemo(() => {
        if (!currentMetricData) {
            return { chartSeries: [], legends: [] };
        }

        // Country name filter function
        const countryNameFilter = (countryId: string | number) => countryTextByIdFilter()(countryId);

        const filteredData = filterChartData(currentMetricData, selectedCountries, originalOrderedCountryIds);

        const series = transformChartDataToSeries(
            filteredData,
            selectedCountries,
            countryNameFilter,
            originalOrderedCountryIds,
        );

        const legendItems = generateLegendItems(
            selectedCountries,
            filteredData,
            countryNameFilter,
            originalOrderedCountryIds,
        );

        return {
            chartSeries: series,
            legends: legendItems,
        };
    }, [currentMetricData, selectedCountries, originalOrderedCountryIds]);

    return (
        <WidgetContainer
            withBorder
            minWidth="100%"
            title={t("industry.analysis.geo.overtime.graph.title")}
            tooltip={t("industry.analysis.geo.overtime.graph.title.tooltip")}
            subtitleFilters={subtitleFilters}
            automationId="industry-analysis-geography-traffic-and-engagement"
        >
            <Tabs
                size="large"
                selectedIndex={currentTabIndex}
                onSelect={handleSelectTab}
            >
                <TabList>
                    {METRIC_KEYS.map((metricKey) => (
                        <StyledTab
                            key={metricKey}
                            data-automation={`${metricKey}-tab`}
                        >
                            <Flex
                                gap="8px"
                                paddingY="4px"
                            >
                                <SWReactIcons
                                    iconName={METRIC_CONFIGURATION[metricKey].iconName}
                                    size="xs"
                                />
                                {t(METRIC_CONFIGURATION[metricKey].metricName)}
                            </Flex>
                        </StyledTab>
                    ))}
                </TabList>

                {METRIC_KEYS.map((metricKey) => (
                    <StyledTabPanel key={metricKey}>
                        <IndustryAnalysisGeographyTrafficAndEngagementChart
                            data={chartSeries}
                            legends={legends}
                            isLoading={isLoading}
                            selectedMetric={metricKey}
                        />
                    </StyledTabPanel>
                ))}
            </Tabs>
        </WidgetContainer>
    );
};
